import { useQuery } from '@tanstack/react-query';
import { LogOut, Menu, RefreshCw } from 'lucide-react';
import { clearApiKey } from '@/api/client';
import { getHealth } from '@/api/real-browsers';
import { cn } from '@/lib/cn';

interface Props {
  onMenuClick: () => void;
}

/**
 * Sticky header — system-health pill (otr-core /health) + manual refresh
 * + logout. The same shape as observe's HeaderBar so the two dashboards
 * feel like one product.
 */
export default function HeaderBar({ onMenuClick }: Props) {
  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 10_000,
  });

  const overall = (() => {
    if (isError) return { tone: 'critical', text: 'API unreachable' };
    if (!data) return { tone: 'info', text: 'connecting…' };
    const m = data.services.mongodb.healthy;
    const r = data.services.redis.healthy;
    if (m === false || r === false) return { tone: 'error', text: 'degraded' };
    if (m === null || r === null)   return { tone: 'warn',  text: 'probing' };
    return { tone: 'ok', text: 'healthy' };
  })();

  return (
    <header className="min-h-12 px-2 sm:px-4 pt-safe border-b border-slate-800 flex items-center justify-between gap-2 bg-slate-950/40 backdrop-blur sticky top-0 z-20">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onMenuClick}
          className="md:hidden tap-target -ml-1 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className={cn(
          'inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full ring-1 min-w-0',
          overall.tone === 'ok'       && 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
          overall.tone === 'warn'     && 'bg-yellow-500/10  text-yellow-300  ring-yellow-500/30',
          overall.tone === 'error'    && 'bg-orange-500/10  text-orange-300  ring-orange-500/30',
          overall.tone === 'critical' && 'bg-red-500/15     text-red-300     ring-red-500/40',
          overall.tone === 'info'     && 'bg-sky-500/10     text-sky-300     ring-sky-500/30',
        )}>
          <span className={cn('dot shrink-0',
            overall.tone === 'ok'       && 'bg-emerald-500',
            overall.tone === 'warn'     && 'bg-yellow-500',
            overall.tone === 'error'    && 'bg-orange-500',
            overall.tone === 'critical' && 'bg-red-500',
            overall.tone === 'info'     && 'bg-sky-500',
          )} />
          <span className="truncate">
            <span className="hidden sm:inline">api: </span>{overall.text}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => refetch()}
          className="tap-target rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
        </button>
        <button
          onClick={() => { clearApiKey(); window.location.href = 'https://access.otrtech.co.uk/web/logout'; }}
          className="tap-target rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}
